import React, { useContext, useEffect, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import {
  CompanyLocationRampsConfigInDTO,
  CompanyLocationRampsOrderInDTO,
  CompanyLocationRampWithOpeningHoursOutDTO,
  CompanyLocationsDetailOutDTO,
  DayEnum,
  OpeningModeEnum,
  Pageable,
} from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import SectionPanel from '@components/framework/panels/SectionPanel.tsx';
import SectionEmptyState from '@app/pages/customer/LocalityDetail/sections/SectionEmptyState.tsx';
import { Ringil3BadgeRow } from '@components/ringil3/styles.tsx';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import RingilDataTable, { ColumnConfig, PagedData } from '@components/RingilDataTable/RingilDataTable.tsx';
import SecuredArea from '@components/security/SecuredArea';
import AddEditRamp from '@app/pages/customer/LocalityList/sidebars/AddEditRamp.tsx';
import LocationConcurrentActionsTag from '@components/ringil3/Features/Location/Tags/LocationConcurrentActionsTag.tsx';
import LocationTvDashboardTag from '@components/ringil3/Features/Location/Tags/LocationTvDashboardTag.tsx';
import LocationTvLinkTag from '@components/ringil3/Features/Location/Tags/LocationTvLinkTag.tsx';
import { isNullOrUndefined } from '@utils/utils.tsx';
import COBadge from '@components/FigmaComponents/COBadge.tsx';
import SvgArrivalTruck from '@icons/arrival-truck.svg?react';
import SvgPlus from '@icons/plus.svg?react';
import SvgPencil from '@icons/pencil-inverted.svg?react';
import SvgEye from '@icons/eye.svg?react';
import SvgEyeSlash from '@icons/eye-slash.svg?react';
import SvgChevronRight from '@icons/chevron-right.svg?react';
import SvgArrowUp from '@icons/arrow-up.svg?react';
import useNavigation from '@hooks/useNavigation.tsx';

interface ComponentProps {
  location: CompanyLocationsDetailOutDTO;
  onReload: () => void;
}

const titleCaseDay = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s);

const DAY_COLUMNS: { day: DayEnum; header: string; headerDefault: string }[] = [
  { day: DayEnum.MONDAY, header: 'TimeIntervalComponent.daysMONDAY', headerDefault: 'Monday' },
  { day: DayEnum.TUESDAY, header: 'TimeIntervalComponent.daysTUESDAY', headerDefault: 'Tuesday' },
  { day: DayEnum.WEDNESDAY, header: 'TimeIntervalComponent.daysWEDNESDAY', headerDefault: 'Wednesday' },
  { day: DayEnum.THURSDAY, header: 'TimeIntervalComponent.daysTHURSDAY', headerDefault: 'Thursday' },
  { day: DayEnum.FRIDAY, header: 'TimeIntervalComponent.daysFRIDAY', headerDefault: 'Friday' },
  { day: DayEnum.SATURDAY, header: 'TimeIntervalComponent.daysSATURDAY', headerDefault: 'Saturday' },
  { day: DayEnum.SUNDAY, header: 'TimeIntervalComponent.daysSUNDAY', headerDefault: 'Sunday' },
];

const RampsSection: React.FC<ComponentProps> = ({ location, onReload }) => {
  const { tr } = useTranslationLgs();
  const navigation = useNavigation();
  const { updateLocationRampsConfig, deleteRampForLocation, activateRampForLocation, updateRampsOrder } = useContext(ApiContext);
  const [visibleAddRamp, setVisibleAddRamp] = useState(false);
  const [editRampId, setEditRampId] = useState<string>();
  const [forceHideRampId, setForceHideRampId] = useState<string>();
  const tableRef = useRef<{ reloadData: () => void; selectedObjects: unknown[]; clear: () => void }>(null);

  // Detail DTO ramps[] (unlike the standalone GET /ramps) includes soft-deleted ramps, needed for the Hidden (Skrytá) badge rows.
  const ramps = location.ramps ?? [];

  // Internal-TV link is shown only when the panel has content = >=1 non-deleted ramp on the dashboard (the only rule that
  // produces a non-empty panel; show* columns are display-only). Prod: ~0.8% of locations qualify, so always-show is wrong.
  // Derived from the detail DTO's ramps[] using the BE-confirmed rule — no BE flag needed.
  const internalTvAvailable = {
    type: 'data' as const,
    label: 'A ramp is shown on the internal TV dashboard',
    satisfied: ramps.some(r => r.isShowOnRampDashboard && !r.isSoftDeleted),
  };

  useEffect(() => {
    tableRef.current?.reloadData();
  }, [location]);

  const notPartnerLocation = { type: 'data' as const, label: 'Not a partner location', satisfied: !location.isPartnerLocation };
  // A deleted location is read-only everywhere — Restore on the detail is the only action offered.
  const notDeleted = { type: 'data' as const, label: 'Location is not deleted', satisfied: !location.isDeleted };

  const saveRampsConfig = (overrides: Partial<CompanyLocationRampsConfigInDTO>) => {
    const body: CompanyLocationRampsConfigInDTO = {
      concurrentlyActiveEventsCount: location.concurrentlyActiveEventsCount,
      excludePrivateRampsFromCapacity: location.excludePrivateRampsFromCapacity,
      tvDashboard: location.tvDashboard,
      ...overrides,
    };
    updateLocationRampsConfig(location.id, body, () => onReload());
  };

  const onRampSidebarCompleted = () => {
    onReload();
  };

  // Mirrors legacy softDeleteRamp 1:1: hide = soft delete. On FORCE_NEEDED legacy only records the
  // rampId (no force-retry dialog is wired), so for behavior parity the soft-delete simply doesn't proceed.
  const hideRamp = (rampId: string) => {
    deleteRampForLocation(location.id, rampId, false, true, () => onReload(), null, {
      onAdditionalAction: (data: { action: string }) => {
        if (data.action === 'FORCE_NEEDED') setForceHideRampId(rampId);
      },
    });
  };

  const activateRamp = (rampId: string) => {
    activateRampForLocation(location.id, rampId, () => onReload());
  };

  const rampIndex = (rampId: string) => ramps.findIndex(r => r.rampId === rampId);

  // The order is held over the location's whole ramps[], not over the page the row happens to sit on,
  // so moving a row across a page boundary behaves the same as moving it inside one. The BE takes the
  // complete ordered list of ids, so a swap is sent as the reordered array and the detail reloaded after it.
  const moveRamp = (rampId: string, offset: -1 | 1) => {
    const index = rampIndex(rampId);
    const targetIndex = index + offset;
    if (index < 0 || targetIndex < 0 || targetIndex >= ramps.length) return;

    const rampIds = ramps.map(r => r.rampId);
    [rampIds[index], rampIds[targetIndex]] = [rampIds[targetIndex], rampIds[index]];
    const body: CompanyLocationRampsOrderInDTO = { rampIds };
    updateRampsOrder(location.id, body, () => onReload());
  };

  const renderDay = (row: CompanyLocationRampWithOpeningHoursOutDTO, day: DayEnum) => {
    const dayDef = row.openingHourDefinitions?.openingHoursDays?.find(d => d.day === day);
    if (isNullOrUndefined(dayDef) || dayDef.openingMode === OpeningModeEnum.CLOSED) {
      return <span className="text-content-tertiary">{tr('LocalityDetail.closed', 'Closed')}</span>;
    }
    if (dayDef.openingMode === OpeningModeEnum.OPEN) {
      return <span>{tr('LocalityDetail.allDay', 'non-stop')}</span>;
    }
    return (
      <div className="flex flex-col gap-1">
        {dayDef.openingHours.map((t, i) => (
          <span key={i}>
            {t.since}–{t.till}
          </span>
        ))}
      </div>
    );
  };

  const columnConfig: ColumnConfig<CompanyLocationRampWithOpeningHoursOutDTO>[] = [
    {
      id: 'name',
      header: 'LocalityDetail.ramp',
      headerDefault: 'Ramp',
      width: '18%',
      order: 1,
      render: row => (
        <div className="flex flex-col items-start gap-2">
          <span>{row.name}</span>
          {row.isPrivate && (
            <COBadge variant={'filled'} type={'custom'} className={'bg-content-secondary text-white'} dataTestId={`location-ramp-flag-internal-${row.rampId}`}>
              {tr('LocalityDetail.rampInternal', 'Internal')}
            </COBadge>
          )}
          {row.isShowOnRampDashboard && (
            <COBadge variant={'filled'} type={'custom'} className={'bg-complementary-processing text-content-primary'} dataTestId={`location-ramp-flag-tv-${row.rampId}`}>
              {tr('LocalityDetail.rampInternalTv', 'Internal TV')}
            </COBadge>
          )}
          {row.isTemporaryClosed && (
            <COBadge variant={'filled'} type={'custom'} className={'bg-complementary-errorMedium text-content-primary'} dataTestId={`location-ramp-flag-closed-${row.rampId}`}>
              {tr('LocalityDetail.rampTemporarilyClosed', 'Temporarily closed')}
            </COBadge>
          )}
          {row.isSoftDeleted && (
            <COBadge variant={'filled'} type={'custom'} className={'bg-content-brandMedium text-content-primary'} dataTestId={`location-ramp-flag-hidden-${row.rampId}`}>
              {tr('LocalityDetail.rampHidden', 'Hidden')}
            </COBadge>
          )}
        </div>
      ),
    },
    ...DAY_COLUMNS.map((d, i) => ({
      id: `day-${d.day}`,
      header: titleCaseDay(tr(d.header, d.headerDefault)),
      width: '10%',
      order: i + 2,
      render: (row: CompanyLocationRampWithOpeningHoursOutDTO) => renderDay(row, d.day),
    })),
    {
      id: 'actions',
      width: '7rem',
      order: 9,
      // The two groups wrap as units, so on a narrow screen the ordering buttons keep the first line
      // and the rest move down together rather than the row breaking at an arbitrary button.
      render: row => (
        <div className="flex flex-row flex-wrap justify-end gap-2">
          <div className="flex flex-row gap-2">
            <SecuredArea id="location-ramps-moveUp" instance={row.rampId} label={'Move ramp up'} visible={[notPartnerLocation, notDeleted]}>
              <RingilButton
                mode={'table'}
                icon={<SvgArrowUp />}
                tooltip={tr('LocalityDetail.moveRampUp', 'Move up')}
                disabled={rampIndex(row.rampId) <= 0}
                onClick={() => moveRamp(row.rampId, -1)}
                dataTestId={`location-ramps-moveUp-${row.rampId}`}
              />
            </SecuredArea>
            {/* `arrow-down.svg` hardcodes a grey fill, so the up arrow is reused rotated. */}
            <SecuredArea id="location-ramps-moveDown" instance={row.rampId} label={'Move ramp down'} visible={[notPartnerLocation, notDeleted]}>
              <RingilButton
                mode={'table'}
                icon={<SvgArrowUp className={'rotate-180'} />}
                tooltip={tr('LocalityDetail.moveRampDown', 'Move down')}
                disabled={rampIndex(row.rampId) === ramps.length - 1}
                onClick={() => moveRamp(row.rampId, 1)}
                dataTestId={`location-ramps-moveDown-${row.rampId}`}
              />
            </SecuredArea>
          </div>
          <div className="flex flex-row gap-2">
            <SecuredArea id="location-ramps-edit" instance={row.rampId} label={'Edit ramp'} visible={[notPartnerLocation, notDeleted]}>
              <RingilButton mode={'table'} icon={<SvgPencil />} onClick={() => setEditRampId(row.rampId)} dataTestId={`location-ramps-edit-${row.rampId}`} />
            </SecuredArea>
            {row.isSoftDeleted ? (
              <SecuredArea id="location-ramps-activate" instance={row.rampId} label={'Activate ramp'} visible={[notPartnerLocation, notDeleted]}>
                <RingilButton mode={'table'} icon={<SvgEye className={'[&_path]:fill-current'} />} onClick={() => activateRamp(row.rampId)} dataTestId={`location-ramps-activate-${row.rampId}`} />
              </SecuredArea>
            ) : (
              <SecuredArea id="location-ramps-hide" instance={row.rampId} label={'Hide ramp'} visible={[notPartnerLocation, notDeleted]}>
                <RingilButton mode={'table'} icon={<SvgEyeSlash className={'[&_path]:fill-current'} />} onClick={() => hideRamp(row.rampId)} dataTestId={`location-ramps-hide-${row.rampId}`} />
              </SecuredArea>
            )}
            <RingilButton
              mode={'table'}
              icon={<SvgChevronRight />}
              onClick={() => navigation.navigate(navigation.urlFunctions.createRampDetail(location.id, row.rampId))}
              dataTestId={`location-ramps-detail-${row.rampId}`}
            />
          </div>
        </div>
      ),
    },
  ];

  const loadTableData = (
    filter: unknown,
    pageable: Pageable,
    onSuccess: (pagedData: PagedData<CompanyLocationRampWithOpeningHoursOutDTO>) => void,
  ) => {
    const page = pageable.pageNumber || 1;
    const size = pageable.pageSize || 10;
    const start = (page - 1) * size;
    onSuccess({ data: ramps.slice(start, start + size), total: ramps.length, page, size });
  };

  return (
    <>
      <SectionPanel
        icon={<SvgArrivalTruck className={'w-6 h-6'} />}
        iconColor={'var(--content-tertiary)'}
        heading={
          <span className="flex flex-row items-center gap-2">
            {tr('LocalityDetail.ramps', 'Ramps')}
            <SecuredArea id={'location-ramps-new'} label={'New ramp'} visible={[notPartnerLocation, notDeleted]}>
              <RingilButton
                mode={'link'}
                icon={<SvgPlus />}
                label={tr('LocalityDetail.newRamp', 'New ramp')}
                onClick={() => setVisibleAddRamp(true)}
                dataTestId={'location-ramps-new'}
              />
            </SecuredArea>
          </span>
        }
        headerRight={<COTimezone timezoneId={location.timezoneId} dataTestId={'location-ramps-timezone'} />}>
        <Ringil3BadgeRow>
          <LocationConcurrentActionsTag
            concurrentlyActiveEventsCount={location.concurrentlyActiveEventsCount}
            excludePrivateRampsFromCapacity={location.excludePrivateRampsFromCapacity}
            onChange={v => saveRampsConfig({ concurrentlyActiveEventsCount: v.concurrentlyActiveEventsCount, excludePrivateRampsFromCapacity: v.excludePrivateRampsFromCapacity })}
            readOnly={location.isDeleted}
            dataTestId={'location-tag-concurrentActions'}
          />
          <LocationTvDashboardTag
            value={location.tvDashboard}
            maxColumns={location.tvMaxColumns}
            onChange={v => saveRampsConfig({ tvDashboard: v })}
            readOnly={location.isDeleted}
            dataTestId={'location-tag-tvDashboard'}
          />
          <SecuredArea id={'location-tag-tvLink'} label={'Internal TV link'} visible={[internalTvAvailable]}>
            <LocationTvLinkTag locationId={location.id} dataTestId={'location-tag-tvLink'} />
          </SecuredArea>
        </Ringil3BadgeRow>

        {ramps.length === 0 ? (
          <SectionEmptyState
            message={tr('LocalityDetail.noRamps', 'You have no ramps added.')}
            buttonLabel={tr('LocalityDetail.addRamp', 'Add ramp')}
            onButtonClick={() => setVisibleAddRamp(true)}
            secured={{ id: 'location-ramps-add-empty', label: 'Add ramp', editable: [notPartnerLocation, notDeleted] }}
            dataTestId={'location-ramps-empty'}
          />
        ) : (
          <RingilDataTable
            ref={tableRef}
            columnConfig={columnConfig as ColumnConfig<unknown>[]}
            loadDataFromApi={loadTableData}
            filterName={'location_detail_ramps'}
          />
        )}
      </SectionPanel>

      <AddEditRamp
        visible={visibleAddRamp}
        onHide={() => setVisibleAddRamp(false)}
        onCompleted={() => {
          setVisibleAddRamp(false);
          onRampSidebarCompleted();
        }}
        locationId={location.id}
        timezoneId={location.timezoneId}
      />

      <AddEditRamp
        visible={!!editRampId}
        onHide={() => setEditRampId(undefined)}
        onCompleted={() => {
          setEditRampId(undefined);
          onRampSidebarCompleted();
        }}
        locationId={location.id}
        timezoneId={location.timezoneId}
        editRampId={editRampId}
      />
    </>
  );
};

export default RampsSection;
